import { type z } from "zod";
import { type EntityType, type FlowEntity, type FlowEntityType } from "../../common/types";
import { type emailSchema } from "../../Emails/types/schemas";
import { type eventSchema } from "../../Events/types/schemas";
import { type CompletionStatus } from "../../Flows/types/enums";
import {
  type assessmentQuestionSchema,
  type assessmentSchema,
  type assessmentWithQuestionsSchema,
  type closedCaptions,
  type embeddedVideoPreviewSchema,
  type embeddedVideoSchema,
  type flowEndAssetSchema,
  type htmlAssetSchema,
  type messageSchema,
  type pdfAssetSchema,
  type surveySchema,
  type surveyWithQuestionsSchema,
  type videoAssetSchema,
  type videoPreviewAssetSchema,
  type videoStreamManifestSchema,
} from "./schemas";

export type VideoAsset = z.infer<typeof videoAssetSchema> & {
  type: EntityType.Video;
};

export type VideoPreviewAsset = z.infer<typeof videoPreviewAssetSchema> & {
  type: EntityType.Video;
  manifests: z.infer<typeof videoStreamManifestSchema>[];
  closedCaptions: z.infer<typeof closedCaptions>[];
};

export type VideoAssetWithManifest = z.infer<typeof videoAssetSchema> & {
  type: EntityType.Video;
  manifests: z.infer<typeof videoStreamManifestSchema>[];
  closedCaptions: z.infer<typeof closedCaptions>[];
};

export type EmbeddedVideoAsset = z.infer<typeof embeddedVideoSchema> & {
  type: EntityType.EmbeddedVideo;
};

export type EmbeddedVideoPreview = z.infer<typeof embeddedVideoPreviewSchema> & {
  type: EntityType.EmbeddedVideo;
};

export type PdfAsset = z.infer<typeof pdfAssetSchema> & {
  type: EntityType.Pdf;
};

export type AssessmentAsset = z.infer<typeof assessmentWithQuestionsSchema> & {
  type: EntityType.Assessment;
};

export type Assessment = z.infer<typeof assessmentSchema>;

export type SurveyAsset = z.infer<typeof surveyWithQuestionsSchema> & {
  type: EntityType.Survey;
  questions: SurveyQuestion[];
};

export type Survey = z.infer<typeof surveySchema>;

export type HtmlAsset = z.infer<typeof htmlAssetSchema> & {
  type: EntityType.Html;
};

export type ExternalEvent = z.infer<typeof eventSchema> & {
  type: EntityType.Event;
};

export type Email = z.infer<typeof emailSchema> & {
  type: EntityType.Email;
};

export type MessageAsset = z.infer<typeof messageSchema> & {
  type: EntityType.Message;
};

export type FlowEndAsset = z.infer<typeof flowEndAssetSchema> & {
  type: FlowEntityType.FlowEnd;
};

export type UnavailableAsset = {
  originType: FlowEntity;
  id: number;
  title: string;
  type: FlowEntityType.UnavailableAsset;
};

export type Asset =
  | VideoAsset
  | EmbeddedVideoAsset
  | PdfAsset
  | AssessmentAsset
  | SurveyAsset
  | HtmlAsset
  | ExternalEvent
  | Email
  | MessageAsset
  | FlowEndAsset
  | UnavailableAsset;

export type AssessmentQuestion = z.infer<typeof assessmentQuestionSchema>;

export interface QuestionsAnswers {
  [key: number]: boolean[];
}

export interface VideoStreamManifest {
  type: string;
  uri: string;
}

export interface ClosedCaptions {
  isoCode: string;
  languageId: number;
  url?: string;
}

export interface AutoTranslatePayload {
  id: number;
  fileName: string;
  isAutoTranslate: boolean;
  languageId: number;
  url: string;
}

export interface Playlist {
  onNext?: () => void;
  onPrevious?: () => void;
  canNext?: boolean;
  canPrevious?: boolean;
  onVideoStarted?: () => void;
  onVideoEnded?: () => void;
  id?: number;
}

export interface RelatedContentItem {
  title: string;
  thumbnailUrl: string;
  metaText1: string;
  metaText2: string;
}

export type SurveyAnswer = {
  id: string;
  type: string;
  answer: string;
};

export type RatingScale = {
  maxRateLabel?: string;
  minRateLabel?: string;
  text: string;
};

export type OtherAnswerOptions = {
  includeOtherAsAnswer: boolean;
  includeUserTextField: boolean;
  isUserTextFieldRequired: boolean;
};

interface BaseQuestion<TType, TQuestion = string> {
  id: string;
  type: TType;
  question: TQuestion;
  isRequired: boolean;
  answers: SurveyAnswer[];
}

export type WithOtherOptions<TType extends BaseQuestion<string, string>> = TType & {
  otherAnswerOptions: OtherAnswerOptions;
};

export enum QuestionTypes {
  MultipleChoice = "MultipleChoiceTextQuestion",
  SingleChoice = "SingleChoiceTextQuestion",
  FreeText = "FreeText",
  RatingScale = "RatingScale",
}

export type MultipleChoiceQuestion = WithOtherOptions<BaseQuestion<QuestionTypes.MultipleChoice>>;
export type SingleChoiceQuestion = WithOtherOptions<BaseQuestion<QuestionTypes.SingleChoice>>;
export type FreeTextQuestion = BaseQuestion<QuestionTypes.FreeText>;
export type RatingScaleQuestion = BaseQuestion<QuestionTypes.RatingScale, RatingScale>;

export type SurveyQuestion = MultipleChoiceQuestion | SingleChoiceQuestion | FreeTextQuestion | RatingScaleQuestion;

export interface SurveyQuestionsAnswers {
  [key: number]: SurveyQuestionAnswer;
}

export type QuestionAnswer = boolean | string | number;
export type OtherAnswer = {
  checked: boolean;
  value?: string | null;
} | null;

export interface SurveyQuestionAnswer {
  answers: QuestionAnswer[];
  otherText: OtherAnswer;
}

export interface SurveyAnswers {
  answeredQuestions: AnsweredSurveyQuestion[];
}

export interface AnsweredSurveyQuestion {
  id: string;
  otherOptionAnswer: OtherOptionAnswer | null;
  answers: SelectedAnswer[];
}

interface SelectedAnswer {
  id?: string;
  value: QuestionAnswer;
}

interface OtherOptionAnswer {
  value?: string | null;
  isChecked: boolean;
}

export interface PdfAssetStandAloneDetails {
  description: string;
  fileName: string;
  id: number;
  pdfDocumentUrl: string;
  title: string;
  completionStatus: CompletionStatus;
}

export interface PdfFlowDetails {
  bag: {
    isDelayed: boolean;
    isBranched: boolean;
  };
  completionStatus: string;
  description: string;
  disabled: boolean;
  expired: boolean;
  fileName: string;
  id: number;
  isDelayTimeNotExpired: boolean;
  order: number;
  pdfDocumentUrl: string;
  permissionIssuedDate: string;
  priorityLevelId: number;
  publisher: string;
  publisherId: number;
  title: string;
  type: string;
}

export type VideoQuestions = {
  // First number refers to minute of the video (ie 0 means 0:00 to 0:59)
  questions: Record<string, string[]>; // {"0": ["question1", "question2"], "1": ["question3"]}
};

export type VideoQuestionsParsed = {
  startTimeSeconds: number;
  endTimeSeconds: number;
  question: string;
};

export enum EmbeddedVideoSource {
  YouTube = 1,
  Vimeo = 2,
  Wistia = 3,
  Vidyard = 4,
}
